/**
 * Adds a bank account to a party. The scheme picks the fields the account needs; a refusal of the
 * account itself (invalid, already there) is shown at its identity field.
 */
import { useState } from 'react';
import {
  Checkbox,
  CountrySelector,
  CurrencySelector,
  FormModal,
  Input,
  InputWrapper,
  Select
} from '@insio/ui-toolkit';
import type { BankAccountScheme } from '@/graphql';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { useEnumTranslation } from '@/hooks/useEnumTranslation';
import { useToastHelpers } from '@/hooks/use-toast-helpers';
import { useBankAccountMutations } from '../hooks/use-bank-account-mutations';
import { BankAccountError, bankAccountError, bankingErrorCodes } from '../utils/bank-account-error';
import {
  BANK_ACCOUNT_FIELD_MAX_LENGTH,
  BANK_ACCOUNT_SCHEMES,
  BIC_MAX_LENGTH,
  bankingErrorFields,
  BankAccountSchemeValue,
  LABEL_MAX_LENGTH,
  emptyBankAccountDraft,
  identityField,
  missingFields,
  schemeFields,
  toCreateInput,
  type BankAccountDraft,
  type BankAccountField
} from '../utils/bank-account-form';

/** Schemes whose account number may carry a `prefix-`. */
const DOMESTIC_PREFIX_SCHEMES: readonly BankAccountScheme[] = [
  BankAccountSchemeValue.CZ,
  BankAccountSchemeValue.SK
];

/** The banking validation codes with a message of their own under `partyBankAccounts.bankingErrors`. */
const BANKING_ERROR_MESSAGE_CODES: readonly string[] = [
  'required',
  'invalidFormat',
  'unknownBankCode',
  'prefixMod11Failed',
  'accountMod11Failed',
  'ibanRequired',
  'ibanTooShort',
  'ibanInvalidCountryCode',
  'ibanInvalid',
  'bicRequired',
  'bicInvalidLength',
  'bicInvalid',
  'schemeUnknown',
  'countryCodeInvalid',
  'countryMismatch',
  'schemeRequiredForCountry',
  'bankIdentifierRequired',
  'bankIdentifierInvalid',
  'accountNumberRequired',
  'accountNumberInvalid',
  'routingChecksumFailed'
];

interface AddBankAccountModalProps {
  partyId: string;
  onClose: () => void;
}

export function AddBankAccountModal({ partyId, onClose }: AddBankAccountModalProps) {
  const t = useFeatureTranslation('partyBankAccounts.form');
  const tErrors = useFeatureTranslation('partyBankAccounts.errors');
  const tToast = useFeatureTranslation('partyBankAccounts.toast');
  const tScheme = useEnumTranslation('bankAccountScheme');
  const tBanking = useFeatureTranslation('partyBankAccounts.bankingErrors');
  const { showSuccess } = useToastHelpers();
  const { create } = useBankAccountMutations();

  const [draft, setDraft] = useState<BankAccountDraft>(emptyBankAccountDraft);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<BankAccountField, string>>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const setField = <K extends keyof BankAccountDraft>(field: K, value: BankAccountDraft[K]) => {
    setDraft(previous => ({ ...previous, [field]: value }));
    setFieldErrors(previous => ({ ...previous, [field]: undefined }));
  };

  const changeScheme = (scheme: BankAccountScheme) => {
    setDraft(previous => ({ ...previous, scheme }));
    setFieldErrors({});
  };

  const handleSubmit = async () => {
    setFormError(null);
    const missing = missingFields(draft);
    if (missing.length > 0) {
      setFieldErrors(Object.fromEntries(missing.map(field => [field, t('required')])));
      return;
    }
    setSaving(true);
    try {
      await create(toCreateInput(partyId, draft));
      showSuccess(tToast('created'));
      onClose();
    } catch (err) {
      const refusal = bankAccountError(err);
      if (refusal === BankAccountError.INVALID) {
        setFieldErrors(invalidFieldErrors(bankingErrorCodes(err)));
      } else if (refusal === BankAccountError.DUPLICATE) {
        setFieldErrors({ [identityField(draft.scheme)]: tErrors(refusal) });
      } else {
        setFormError(tErrors(refusal));
      }
    } finally {
      setSaving(false);
    }
  };

  /** Each banking code at its field; a refusal that names none stays at the main field. */
  const invalidFieldErrors = (codes: readonly string[]) => {
    if (codes.length === 0)
      return { [identityField(draft.scheme)]: tErrors(BankAccountError.INVALID) };
    const byField = bankingErrorFields(draft.scheme, codes);
    return Object.fromEntries(
      Object.entries(byField).map(([field, fieldCodes]) => [
        field,
        (fieldCodes ?? []).map(code => bankingErrorMessage(code)).join(' ')
      ])
    );
  };

  /** A banking code's message; a code without one reads as the account not checking out. */
  const bankingErrorMessage = (code: string): string =>
    BANKING_ERROR_MESSAGE_CODES.includes(code) ? tBanking(code) : tErrors(BankAccountError.INVALID);

  const fieldLabel = (field: BankAccountField): string =>
    field === 'bankIdentifier' ? t(`fields.bankIdentifier.${draft.scheme}`) : t(`fields.${field}`);

  const renderField = (field: BankAccountField) => {
    const id = `bank-account-${field}`;
    const error = fieldErrors[field];
    const hint =
      field === 'accountNumber' && DOMESTIC_PREFIX_SCHEMES.includes(draft.scheme)
        ? t('hints.domesticAccountNumber')
        : undefined;
    return (
      <InputWrapper
        key={field}
        label={fieldLabel(field)}
        htmlFor={id}
        labelSize='sm'
        isError={!!error}
        errorText={error}
        hint={hint}
      >
        {field === 'countryCode' ? (
          <CountrySelector
            id={id}
            testId={id}
            value={draft.countryCode}
            onChange={code => setField('countryCode', code)}
            isSearchable
            hasError={!!error}
          />
        ) : (
          <Input
            id={id}
            testId={id}
            value={draft[field]}
            maxLength={field === 'bic' ? BIC_MAX_LENGTH : BANK_ACCOUNT_FIELD_MAX_LENGTH}
            autoComplete='off'
            hasError={!!error}
            onChange={event => setField(field, event.target.value)}
          />
        )}
      </InputWrapper>
    );
  };

  return (
    <FormModal
      open
      testId='add-bank-account-modal'
      title={t('addTitle')}
      description={t('addDescription')}
      submitLabel={t('submitAdd')}
      cancelLabel={t('cancel')}
      loading={saving}
      error={formError}
      onSubmit={handleSubmit}
      onClose={onClose}
    >
      <div className='flex flex-col gap-md'>
        <InputWrapper label={t('fields.scheme')} htmlFor='bank-account-scheme' labelSize='sm'>
          <Select
            id='bank-account-scheme'
            testId='bank-account-scheme'
            value={draft.scheme}
            options={BANK_ACCOUNT_SCHEMES.map(scheme => ({
              value: scheme,
              label: tScheme(scheme)
            }))}
            onChange={value => {
              const scheme = BANK_ACCOUNT_SCHEMES.find(candidate => candidate === value);
              if (scheme) changeScheme(scheme);
            }}
          />
        </InputWrapper>

        {schemeFields(draft.scheme).map(renderField)}

        <InputWrapper label={t('fields.label')} htmlFor='bank-account-label' labelSize='sm'>
          <Input
            id='bank-account-label'
            testId='bank-account-label'
            value={draft.label}
            maxLength={LABEL_MAX_LENGTH}
            onChange={event => setField('label', event.target.value)}
          />
        </InputWrapper>

        <InputWrapper
          label={t('fields.currencyCode')}
          htmlFor='bank-account-currency'
          labelSize='sm'
        >
          <CurrencySelector
            id='bank-account-currency'
            testId='bank-account-currency'
            value={draft.currencyCode}
            unselectedOptionLabel={t('anyCurrency')}
            onChange={code => setField('currencyCode', code)}
          />
        </InputWrapper>

        <Checkbox
          id='bank-account-default'
          testId='bank-account-default'
          label={t('fields.isDefault')}
          checked={draft.isDefault}
          onChange={checked => setField('isDefault', checked)}
        />
      </div>
    </FormModal>
  );
}
